export type AskResponse = {
  answer: string | []
  citations: Citation[]
  generated_chart: string | null
  error?: string
  message_id?: string
  feedback?: Feedback
  exec_results?: ExecResults[]
}

export type Citation = {
  part_index?: number
  content: string
  id: string
  title: string | null
  filepath: string | null
  url: string | null
  metadata: string | null
  chunk_id: string | null
  reindex_id: string | null
}

export type ToolMessageContent = {
  citations: Citation[]
  intent: string
}

export type AzureSqlServerExecResult = {
  intent: string
  search_query: string | null
  search_result: string | null
  code_generated: string | null
  code_exec_result?: string | undefined
}

export type AzureSqlServerExecResults = {
  all_exec_results: AzureSqlServerExecResult[]
}

export type ChatMessage = {
  id: string
  role: string
  content: string | [{ type: string; text: string }, { type: string; image_url: { url: string } }]
  end_turn?: boolean
  date: string
  feedback?: Feedback
  context?: string
  isPrompt?: any
}

export type ExecResults = {
  intent: string
  search_query: string | null
  search_result: string | null
  code_generated: string | null
}

export type Conversation = {
  id: string
  title: string
  messages: ChatMessage[]
  date: string
}

export enum ChatCompletionType {
  ChatCompletion = 'chat.completion',
  ChatCompletionChunk = 'chat.completion.chunk'
}

export type ChatResponseChoice = {
  messages: ChatMessage[]
}

export type ChatResponse = {
  id: string
  model: string
  created: number
  object: ChatCompletionType
  choices: ChatResponseChoice[]
  history_metadata: {
    conversation_id: string
    title: string
    date: string
  }
  error?: any
}

export type ConversationRequest = {
  messages: ChatMessage[]
}

export type UserInfo = {
  access_token: string
  expires_on: string
  id_token: string
  provider_name: string
  user_claims: any[]
  user_id: string
  auth_provider?: string  // Added for Circle SSO
  user_name?: string  // Added for Circle SSO
  user_principal_id?: string  // Added for unified auth
}

export enum CosmosDBStatus {
  NotConfigured = 'CosmosDB is not configured',
  NotWorking = 'CosmosDB is not working',
  InvalidCredentials = 'CosmosDB has invalid credentials',
  InvalidDatabase = 'Invalid CosmosDB database name',
  InvalidContainer = 'Invalid CosmosDB container name',
  Working = 'CosmosDB is configured and working'
}

export type CosmosDBHealth = {
  cosmosDB: boolean
  status: string
}

export enum ChatHistoryLoadingState {
  Loading = 'loading',
  Success = 'success',
  Fail = 'fail',
  NotStarted = 'notStarted'
}

export type ErrorMessage = {
  title: string
  subtitle: string
}

export type UI = {
    title: string;
    chat_title: string;
    chat_description: string;
    logo?: string;
    chat_logo?: string;
    show_share_button?: boolean;
    share_button_text?: string;
    share_button_link?: string;
    show_web_scrape?: boolean;
    header_color?: string;
    input_border_color?: string;
    input_background_color?: string;
    chat_color?: string;
    chat_text_color?: string;
    title_text_color?: string;
    improve_button_color?: string;
    show_improve_button?: boolean;
    stop_generating_color?: string;
    show_chat_logo?: boolean;
    show_logo?: boolean;
    show_upload_button?: boolean;
    show_history_button?: boolean;
    improve_button_text_color?: string;
    chat_font_empty_state?: string;
    show_citation_panel?: boolean;
    disclaimer_text?: string;
    disclaimer_text_link?: string;
    chat_text_shadow?: boolean;
    show_title?: boolean
    new_chat_button_color?: string;
    automatic_scroll_down?: boolean;
    chat_background_image?: string;
    render_prompt_button_number?: number;
    hide_prompt_icons?: boolean;
    show_pdf_initial_text?: boolean;
    upload_pdf_and_scrape_button_color?: string;
    upload_pdf_and_scrape_button_text_color?: string;
    logo_size?: string;
    prompt9_button_onclick_link?: string;
    prompt10_button_onclick_link?: string;
    chat_background_image_alt?: string;
    share_button_color?: string;
    share_button_text_color?: string;
    show_word_doc_button?: boolean;
    hide_input_on_empty_state?: boolean;
    chat_description_font_size?: string;
} & {
  [key in `prompt${number}_suggestion_text` | 
    `prompt${number}_suggestion_message` | 
    `prompt${number}_header_text`]: string;
}

export type FrontendSettings = {
  auth_enabled?: string | null
  feedback_enabled?: string | null
  ui?: UI
  sanitize_answer?: boolean
  oyd_enabled?: boolean
  storage?: {
    account_name: string
    container_name: string
  }
}

export enum Feedback {
  Neutral = 'neutral',
  Positive = 'positive',
  Negative = 'negative',
  MissingCitation = 'missing_citation',
  WrongCitation = 'wrong_citation',
  OutOfScope = 'out_of_scope',
  InaccurateOrIrrelevant = 'inaccurate_or_irrelevant',
  OtherUnhelpful = 'other_unhelpful',
  HateSpeech = 'hate_speech',
  Violent = 'violent',
  Sexual = 'sexual',
  Manipulative = 'manipulative',
  OtherHarmful = 'other_harmlful'
}
